import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import {
  Search,
  LayoutDashboard,
  Calendar,
  ClipboardList,
  BookOpen,
  CalendarDays,
  FileBarChart,
  Users as UsersIcon,
  Briefcase,
  Settings,
  ShieldAlert,
  Snowflake,
  Zap,
  UserCircle,
  ArrowRight,
  Command,
} from 'lucide-react';

/**
 * Global command palette.
 * — Opens on Ctrl+K / Cmd+K from any authenticated page
 * — Lets the user jump to any page, any configuration card, or search teachers by name
 * — Escape closes, Enter activates first result, ↑/↓ navigate
 */
export const CommandPalette = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Global keybinding
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(v => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when opened + reset query
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 20);
      // Lazy-load teachers list (admin only)
      if (user?.role === 'admin' && teachers.length === 0) {
        axios.get(`${API}/users/teachers-list`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setTeachers(res.data || []))
          .catch(() => {});
      }
    }
  }, [open]);

  const pages = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'teacher', 'exco'] },
    { path: '/absences', label: user?.role === 'admin' ? 'Absences' : 'My Absences', icon: Calendar, roles: ['admin', 'teacher'] },
    { path: '/relief', label: 'Relief Duties', icon: ClipboardList, roles: ['admin', 'teacher'] },
    { path: '/timetables', label: 'Timetables', icon: BookOpen, roles: ['admin', 'teacher'] },
    { path: '/exams', label: 'Exam Invigilation', icon: CalendarDays, roles: ['admin', 'teacher'] },
    { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'exco'] },
    { path: '/users', label: 'Users', icon: UsersIcon, roles: ['admin'] },
    { path: '/job-titles', label: 'Job Titles', icon: Briefcase, roles: ['admin'] },
    { path: '/configuration', label: 'Configuration', icon: Settings, roles: ['admin'] },
    { path: '/compliance', label: 'Compliance', icon: ShieldAlert, roles: ['admin'] },
  ].filter(p => p.roles.includes(user?.role)), [user?.role]);

  // Configuration shortcuts (admin only) — jump to /configuration and highlight card
  const configShortcuts = useMemo(() => {
    if (user?.role !== 'admin') return [];
    return [
      { testid: 'exam-freeze-card', label: 'Freeze Timetable During Exams', keywords: 'freeze exam invigilation release timetable' },
      { testid: 'venues-card', label: 'Venue Management', keywords: 'venue capacity hall room' },
      { testid: 'invigilation-rules-card', label: 'Invigilation Business Rules', keywords: 'invigilation rules ratio standby roaming' },
      { testid: 'class-absences-card', label: 'Class Absences', keywords: 'class absence field trip' },
      { testid: 'daily-briefing-card', label: 'Daily Briefing', keywords: 'daily briefing morning email' },
      { testid: 'section-general', label: 'General & System settings', keywords: 'general timezone locale periods workload' },
      { testid: 'section-notifications', label: 'Notifications settings', keywords: 'notifications email telegram whatsapp resend' },
      { testid: 'section-scheduling', label: 'Scheduling settings', keywords: 'scheduling holidays non-teaching' },
      { testid: 'section-exams', label: 'Exams settings', keywords: 'exam invigilation freeze venue' },
    ];
  }, [user?.role]);

  // Admin quick actions
  const quickActions = useMemo(() => {
    if (user?.role !== 'admin') return [];
    return [
      { id: 'report-absence', label: 'Report Absence', icon: Zap, keywords: 'report absence new create teacher',
        run: () => { navigate('/absences'); setTimeout(() => document.querySelector('[data-testid="report-absence-button"]')?.click(), 300); } },
      { id: 'smart-assign-invig', label: 'Smart Assign Invigilation', icon: Zap, keywords: 'smart assign invigilation',
        run: () => navigate('/exams') },
      { id: 'smart-assign-relief', label: 'Smart Assign Relief', icon: Zap, keywords: 'smart assign relief bulk',
        run: () => navigate('/relief?mode=smart') },
    ];
  }, [user?.role, navigate]);

  // Combine & filter
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesQ = (...fields) => {
      if (!q) return true;
      return fields.some(f => (f || '').toLowerCase().includes(q));
    };

    const out = [];

    pages.forEach(p => {
      if (matchesQ(p.label, p.path)) {
        out.push({
          kind: 'page', key: `page-${p.path}`, icon: p.icon, label: p.label, hint: p.path,
          run: () => navigate(p.path),
        });
      }
    });

    configShortcuts.forEach(s => {
      if (matchesQ(s.label, s.keywords)) {
        out.push({
          kind: 'config', key: `config-${s.testid}`, icon: Snowflake, label: s.label, hint: 'Configuration',
          run: () => {
            navigate('/configuration');
            setTimeout(() => {
              const el = document.querySelector(`[data-testid="${s.testid}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'transition-all');
                setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'transition-all'), 2200);
              }
            }, 450);
          },
        });
      }
    });

    quickActions.forEach(a => {
      if (matchesQ(a.label, a.keywords)) {
        out.push({
          kind: 'action', key: `act-${a.id}`, icon: a.icon, label: a.label, hint: 'Quick action', run: a.run,
        });
      }
    });

    // Teachers — only surface when user has typed ≥ 2 chars (keeps list tidy)
    if (user?.role === 'admin' && q.length >= 2) {
      teachers.forEach(t => {
        if (matchesQ(t.name, t.email)) {
          out.push({
            kind: 'teacher', key: `t-${t.id}`, icon: UserCircle, label: t.name, hint: t.email,
            run: () => navigate(`/users?q=${encodeURIComponent(t.email)}`),
          });
        }
      });
    }

    return out.slice(0, 40);
  }, [query, pages, configShortcuts, quickActions, teachers, user?.role, navigate]);

  // Reset active index if query changes
  useEffect(() => { setActiveIdx(0); }, [query]);

  const activate = (item) => {
    if (!item) return;
    setOpen(false);
    item.run();
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(results[activeIdx]);
    }
  };

  // Scroll active result into view
  useEffect(() => {
    const node = listRef.current?.children?.[activeIdx];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!user) return null;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-slate-900/50 backdrop-blur-sm"
          data-testid="command-palette-overlay"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-xl bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="command-palette"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKey}
                placeholder="Jump to page, setting, teacher, or action..."
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
                data-testid="command-palette-input"
              />
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
                ESC
              </span>
            </div>

            <div ref={listRef} className="max-h-80 overflow-y-auto" data-testid="command-palette-results">
              {results.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">
                  No results for <strong className="text-slate-600">"{query}"</strong>. Try keywords like <em>freeze</em>, <em>reports</em>, or a teacher name.
                </div>
              ) : (
                results.map((r, i) => {
                  const Icon = r.icon || Command;
                  const active = i === activeIdx;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => activate(r)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${active ? 'bg-blue-50 text-blue-900' : 'text-slate-700 hover:bg-slate-50'}`}
                      data-testid={`command-palette-item-${r.key}`}
                    >
                      <Icon className={`h-4 w-4 ${active ? 'text-blue-600' : 'text-slate-400'} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{r.label}</div>
                        {r.hint && <div className="text-[11px] text-slate-400 truncate">{r.hint}</div>}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold ${active ? 'text-blue-600' : 'text-slate-400'}`}>
                        {r.kind}
                      </span>
                      {active && <ArrowRight className="h-3.5 w-3.5 text-blue-600" />}
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400">
              <div className="flex items-center gap-3">
                <span><kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono">↑</kbd> <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono">↓</kbd> navigate</span>
                <span><kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono">↵</kbd> open</span>
              </div>
              <div className="inline-flex items-center gap-1">
                <Command className="h-3 w-3" /> K to toggle
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommandPalette;
